import { Download } from '@mui/icons-material'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { FC, useRef, useState } from 'react'
import { useMinimalsTheme } from '../../../../../hooks'
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '../../../../icons'

export type PdfViewerFileProps = {
  blob: Blob
  fileName?: string
  url: string
}

type PdfViewerProps = {
  files: PdfViewerFileProps[]
  onClose: () => void
}

export const PdfFileViewer: FC<PdfViewerProps> = props => {
  const { files, onClose } = props
  const theme = useMinimalsTheme()

  const iframeRef = useRef<HTMLIFrameElement>(null)

  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [currentFile, setCurrentFile] = useState<PdfViewerFileProps>(files[currentIndex])

  const onDownload = () => {
    const link = document.createElement('a')
    link.href = currentFile.url
    link.download = currentFile.fileName || 'document.pdf'
    link.click()
    URL.revokeObjectURL(currentFile.url)
  }

  const onPrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.focus()
      iframeRef.current.contentWindow?.print()
    }
  }

  const handleNext = () => {
    if (currentIndex < files.length - 1) {
      const newIndex = currentIndex + 1
      setCurrentIndex(newIndex)
      setCurrentFile(files[newIndex])
    }
  }

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1
      setCurrentIndex(newIndex)
      setCurrentFile(files[newIndex])
    }
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: '80px calc(100vh - 80px)',
        backgroundColor: theme.palette.grey[200],
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: theme.customSpacing.m,
          borderBottom: `1px solid ${theme.palette.grey[400]}`,
        }}
      >
        <Box>
          <Typography variant='h2'>{currentFile.fileName}</Typography>
          <Typography variant='body2'>
            Document {currentIndex + 1} out of {files.length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: theme.customSpacing.s }}>
          <Box sx={{ display: 'flex', gap: theme.customSpacing.xs }}>
            <IconButton onClick={handlePrevious} disabled={currentIndex === 0}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={handleNext} disabled={currentIndex === files.length - 1}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Button onClick={onDownload} variant='contained' color='inherit' startIcon={<Download />}>
            Download
          </Button>
          <Button onClick={onPrint} variant='contained' color='primary'>
            Print
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          height: 'calc(100vh - 80px)',
          backgroundColor: theme.palette.background.neutral,
        }}
      >
        <iframe
          ref={iframeRef}
          src={`${currentFile.url}#toolbar=0&navpanes=0&scrollbar=0`}
          style={{ flexGrow: 1, width: '100%', border: 'none', height: 'calc(100% - 8px)' }}
          title='PDF Viewer'
        />
      </Box>
    </Box>
  )
}
